"use client";

import * as React from "react";
import { CircleCheck as CheckCircle2, Clock, ShieldCheck, Wrench, Loader as Loader2, Send } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const benefits = [{
  icon: ShieldCheck,
  title: "Application-Specific Feasibility",
  description: "Evaluations tailored to your specific process parameters and regulatory matrix."
}, {
  icon: Clock,
  title: "Direct Specialist Response",
  description: "Connect directly with our technical spectroscopy and validation engineers within 24 hours."
}, {
  icon: Wrench,
  title: "Lifecycle & Regulatory Alignment",
  description: "Full compliance pathway support including IQ/OQ protocols, 21 CFR Part 11, and on-site servicing."
}];
function InquiryFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center shadow-xs">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="font-default text-xl font-bold text-card-foreground">
          Inquiry Submitted
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sidebar-foreground">Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-sidebar-accent/50 border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sidebar-foreground">Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-sidebar-accent/50 border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sidebar-foreground">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" className="bg-sidebar-accent/50 border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sidebar-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="+44 20 1234 5678" className="bg-sidebar-accent/50 border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Application notes and message are required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sidebar-foreground">Message / Application Notes</FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Your message..." className="bg-sidebar-accent/50 border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full font-medium">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Sending Request...
          </> : <>
            <Send className="mr-2 size-4" />
            Submit Inquiry
          </>}
      </Button>

      <p className="text-center text-xs text-sidebar-foreground/70">
        Confidential technical review. Your information is protected under standard non-disclosure practices.
      </p>
    </Form>;
}
export default function Inquiry() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationinquirycta" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="page-container">
        <div className="overflow-hidden rounded-2xl bg-sidebar text-sidebar-foreground shadow-xl border border-sidebar-border">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Left Column: Copy and Factual Bullet Points */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex flex-col justify-between p-8 sm:p-10 lg:col-span-6 lg:p-12">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-sidebar-accent px-3 py-1 text-xs font-medium tracking-wide text-sidebar-foreground uppercase border border-sidebar-border">
                  Application Consultation
                </div>

                <h2 className="font-default mt-6 text-3xl font-bold tracking-tight text-sidebar-foreground sm:text-4xl">
                  Consult With Our Analytical Specialists
                </h2>

                <p className="mt-4 text-base leading-relaxed text-sidebar-foreground/80">
                  Discuss your process requirements, sample feasibility, or qualification timelines with experienced engineers supporting regulated laboratories across Ireland and the UK.
                </p>

                <div className="mt-8 space-y-6">
                  {benefits.map((item, index) => {
                  const Icon = item.icon;
                  return <div key={item.title} data-index={index} className="flex items-start gap-4">
                        <div className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary/20 text-sidebar-primary border border-sidebar-primary/30">
                          <Icon className="size-5" />
                        </div>
                        <div>
                          <h3 className="font-default text-base font-semibold text-sidebar-foreground">
                            {item.title}
                          </h3>
                          <p className="mt-1 text-sm text-sidebar-foreground/75 leading-normal">
                            {item.description}
                          </p>
                        </div>
                      </div>;
                })}
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-sidebar-border/60">
                <p className="text-xs text-sidebar-foreground/70">
                  Antech Solutions - Application-led analytical technology, qualification and lifecycle support.
                </p>
              </div>
            </motion.div>

            {/* Right Column: Structured Inquiry Form */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut",
            delay: 0.1
          }} className="bg-sidebar-accent/30 p-8 sm:p-10 lg:col-span-6 lg:p-12 lg:border-l lg:border-sidebar-border">
              <Card className="border-0 bg-transparent shadow-none py-0">
                <CardContent className="p-0">
                  <div className="mb-6">
                    <h3 className="font-default text-xl font-bold text-sidebar-foreground">
                      Contact Us - Antech Solutions
                    </h3>
                    <p className="mt-1 text-sm text-sidebar-foreground/75">
                      Provide your application requirements below to initiate technical review.
                    </p>
                  </div>

                  <FormProvider formId="contact_form" sectionName="consultation-inquiry-cta" defaultValues={{
                  name: "",
                  work_email: "",
                  company_name: "",
                  phone_number: "",
                  message: ""
                }} validationRules={{
                  name: {
                    required: true,
                    minLength: 2
                  },
                  work_email: {
                    required: true,
                    pattern: "email"
                  },
                  message: {
                    required: true,
                    minLength: 10
                  }
                }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry. Our application engineers will review your details and respond promptly." errorMessage="Unable to process your inquiry at this moment. Please check your connection and try again.">
                    <InquiryFormFields />
                  </FormProvider>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}